import { useEffect, useState } from "react";

interface ProviderResult {
  ok: boolean;
  latency_ms: number | null;
  error: string | null;
}

interface HealthData {
  ts: number;
  ver: string;
  defaults: {
    provider: string | null;
    model: string | null;
    strategy: string;
  };
  providers: Record<string, ProviderResult>;
}

export default function Diagnostics() {
  const [data, setData] = useState<HealthData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showRaw, setShowRaw] = useState(false);

  async function fetchHealth() {
    setLoading(true);
    setError(null);
    try {
      const resp = await fetch("/api/health/providers");
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const raw = await resp.json() as { success?: boolean; data?: HealthData };
      // The endpoint returns data directly, not wrapped in { success, data }
      setData((raw as any).providers ? raw as HealthData : raw.data as HealthData);
    } catch (e) {
      setError(String(e));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { fetchHealth(); }, []);

  const providerLabels: Record<string, string> = {
    huggingface: "HuggingFace Inference",
    "workers-ai": "Workers AI",
    openai: "OpenAI",
    anthropic: "Anthropic",
    jina: "Jina AI",
    d1: "D1 Database",
  };

  const providerIcons: Record<string, string> = {
    huggingface: "🤗",
    "workers-ai": "🌩",
    openai: "🟢",
    anthropic: "🔴",
    jina: "📐",
    d1: "🗄",
  };

  const allOk = data && Object.values(data.providers).every(p => p.ok);
  const lastUpdated = data ? new Date(data.ts).toLocaleTimeString() : "—";

  return (
    <div className="p-6 max-w-4xl">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Diagnostics</h1>
          <p className="text-sm text-gray-500 mt-1">
            Health status for all configured AI providers and services
          </p>
        </div>
        <div className="flex items-center gap-3">
          {allOk !== null && (
            <span className={`text-sm font-medium ${allOk ? "text-green-600" : "text-red-600"}`}>
              {allOk ? "All OK" : "Issues detected"}
            </span>
          )}
          <button onClick={fetchHealth} disabled={loading} className="btn-primary btn btn-sm">
            {loading ? "Pinging…" : "Refresh"}
          </button>
        </div>
      </div>

      {/* Defaults card */}
      {data && (
        <div className="card p-4 mb-5 grid grid-cols-3 gap-4 text-sm">
          <div>
            <p className="label">Default Provider</p>
            <p className="font-medium">{data.defaults.provider || "none"}</p>
          </div>
          <div>
            <p className="label">Default Model</p>
            <p className="font-medium font-mono text-xs">{data.defaults.model || "none"}</p>
          </div>
          <div>
            <p className="label">Provider Strategy</p>
            <p className="font-medium">{data.defaults.strategy}</p>
          </div>
        </div>
      )}

      {/* Error state */}
      {error && (
        <div className="mb-4 p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700">
          Failed to fetch: {error}
        </div>
      )}

      {/* Provider table */}
      {loading && !data && (
        <div className="text-sm text-gray-400">Pinging providers…</div>
      )}

      {data && (
        <div className="card divide-y divide-gray-100">
          {Object.entries(data.providers).map(([key, result]) => (
            <div key={key} className="flex items-center gap-4 px-5 py-3.5">
              <span className="text-lg w-6 text-center">{providerIcons[key] || "?"}</span>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900">{providerLabels[key] || key}</p>
                <p className="text-xs text-gray-400 font-mono truncate">{key}</p>
              </div>
              <div className="text-right flex-shrink-0">
                {result.ok ? (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
                    OK
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700">
                    FAIL
                  </span>
                )}
              </div>
              <div className="w-20 text-right text-sm text-gray-500 font-mono flex-shrink-0">
                {result.latency_ms != null ? `${result.latency_ms}ms` : "—"}
              </div>
              <div className="w-48 text-right flex-shrink-0">
                {result.error ? (
                  <p className="text-xs text-red-500 truncate max-w-48" title={result.error}>
                    {result.error}
                  </p>
                ) : (
                  <p className="text-xs text-green-500">—</p>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Footer info */}
      <div className="flex items-center justify-between mt-4">
        <p className="text-xs text-gray-400">
          Last checked: {lastUpdated} &middot; v{data?.ver ?? "?"}
        </p>
        <button
          onClick={() => setShowRaw(v => !v)}
          className="text-xs text-gray-400 hover:text-gray-600 underline"
        >
          {showRaw ? "Hide" : "View"} raw JSON
        </button>
      </div>

      {/* Raw JSON toggle */}
      {showRaw && data && (
        <pre className="mt-3 p-4 bg-gray-900 text-gray-100 text-xs font-mono rounded-lg overflow-x-auto max-h-96">
          {JSON.stringify(data, null, 2)}
        </pre>
      )}
    </div>
  );
}